import { useState, useMemo, useEffect } from "react"
import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import {
  MORTGAGE_DEFAULTS,
  calculateMortgage,
  formatCurrency,
  formatCurrencyPence,
} from "@/data/tools/mortgage-defaults"
import { CalculatorResultCard, CalculatorSlider, CalculatorActionsBar } from "@/components/public/calculators"
import { encodeCalculatorParams, decodeCalculatorParams } from "@/lib/calculator-url-params"
import { generateCalculatorPdf } from "@/lib/calculator-pdf"

/* ------------------------------------------------------------------ */
/* FAQ data                                                            */
/* ------------------------------------------------------------------ */

const FAQ = [
  {
    q: "How are monthly mortgage repayments calculated?",
    a: "Repayment mortgages use a standard amortisation formula that distributes payments across the full term. Each monthly payment covers some interest and some capital, with the balance shifting towards more capital over time as the outstanding balance reduces.",
  },
  {
    q: "What is loan-to-value (LTV)?",
    a: "LTV is the percentage of the property price that you borrow. For example, a 90% LTV means you have a 10% deposit. Lower LTVs typically give access to better interest rates because the lender takes on less risk.",
  },
  {
    q: "What is the difference between repayment and interest-only?",
    a: "With a repayment mortgage, each monthly payment covers interest plus a portion of the original loan, so the mortgage is fully paid off at the end of the term. With interest-only, you only pay the interest each month. The original loan amount remains and must be repaid at the end, usually by selling the property or using savings.",
  },
  {
    q: "Does this calculator include fees and insurance?",
    a: "No. This calculator shows principal and interest payments only. Your actual monthly outgoings will also include buildings insurance, potentially life insurance, and any arrangement or product fees that you choose to add to the loan.",
  },
  {
    q: "What mortgage term should I choose?",
    a: "Most UK mortgages run for 25 years, but terms of 30 or 35 years are increasingly common. A longer term means lower monthly payments but more interest paid overall. A shorter term costs more each month but saves thousands in total interest. Use the calculator to compare different terms.",
  },
  {
    q: "What deposit do I need to buy a house in the UK?",
    a: "The minimum deposit is typically 5% of the purchase price, though 10\u201320% will access significantly better interest rates. For a \u00A3300,000 property, that's \u00A315,000 at 5% or \u00A360,000 at 20%. First-time buyers may also be eligible for government schemes that reduce the deposit needed.",
  },
  {
    q: "What is a good mortgage interest rate?",
    a: "Mortgage rates change frequently based on the Bank of England base rate and market conditions. As a rule, rates below the average for your LTV bracket are good. Five-year fixed rates offer stability, while two-year fixes or tracker rates may be lower initially but carry more risk of increases.",
  },
  {
    q: "How much can I borrow for a mortgage?",
    a: "Most lenders offer 4 to 4.5 times your annual household income, though some will stretch to 5 or 5.5 times for higher earners. A household earning \u00A360,000 per year can typically borrow \u00A3240,000\u2013\u00A3270,000. Your actual borrowing limit also depends on existing debts, credit score, and the lender's affordability assessment.",
  },
  {
    q: "Should I overpay my mortgage?",
    a: "Overpaying reduces your outstanding balance faster, saving you interest and potentially shortening your term. Most lenders allow overpayments of up to 10% of the balance per year without early repayment charges. Even small regular overpayments can save thousands over the life of the mortgage.",
  },
  {
    q: "What happens when my fixed rate ends?",
    a: "When your fixed-rate period ends, you'll automatically move to your lender's standard variable rate (SVR), which is usually significantly higher. Most borrowers remortgage to a new fixed rate before the end of their deal. Start looking for new deals around three months before your fix expires.",
  },
]

/* ------------------------------------------------------------------ */
/* Payment split bar                                                   */
/* ------------------------------------------------------------------ */

function PaymentSplitBar({
  loanAmount,
  totalInterest,
}: {
  loanAmount: number
  totalInterest: number
}) {
  const total = loanAmount + totalInterest
  if (total <= 0) return null
  const principalPercent = (loanAmount / total) * 100

  return (
    <div className="rounded-xl border border-stone bg-white p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-charcoal">
        Total repayment breakdown
      </p>
      <div className="mt-3 h-4 overflow-hidden rounded-full bg-stone">
        <div
          className="h-full rounded-full bg-red transition-all duration-300"
          style={{ width: `${principalPercent}%` }}
        />
      </div>
      <div className="mt-2 flex justify-between text-xs">
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-2 rounded-full bg-red" />
          <span className="text-slate">
            Principal: {formatCurrency(loanAmount)} ({principalPercent.toFixed(0)}%)
          </span>
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block size-2 rounded-full bg-stone" />
          <span className="text-slate">
            Interest: {formatCurrency(totalInterest)} ({(100 - principalPercent).toFixed(0)}%)
          </span>
        </span>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function MortgageCalculatorPage({ meta }: { meta: SeoMeta }) {
  const initialParams = useMemo(() => {
    if (typeof window === "undefined") return MORTGAGE_DEFAULTS
    const sp = new URLSearchParams(window.location.search)
    return decodeCalculatorParams(
      {
        propertyPrice: MORTGAGE_DEFAULTS.propertyPrice,
        depositPercent: MORTGAGE_DEFAULTS.depositPercent,
        interestRate: MORTGAGE_DEFAULTS.interestRate,
        termYears: MORTGAGE_DEFAULTS.termYears,
        isInterestOnly: MORTGAGE_DEFAULTS.isInterestOnly,
      },
      sp,
    )
  }, [])

  const [price, setPrice] = useState<number>(initialParams.propertyPrice as number)
  const [deposit, setDeposit] = useState<number>(initialParams.depositPercent as number)
  const [rate, setRate] = useState<number>(initialParams.interestRate as number)
  const [term, setTerm] = useState<number>(initialParams.termYears as number)
  const [interestOnly, setInterestOnly] = useState<boolean>(initialParams.isInterestOnly as boolean)

  const result = calculateMortgage({
    propertyPrice: price,
    depositPercent: deposit,
    interestRate: rate,
    termYears: term,
    isInterestOnly: interestOnly,
  })

  useEffect(() => {
    const params = encodeCalculatorParams({
      propertyPrice: price, depositPercent: deposit, interestRate: rate,
      termYears: term, isInterestOnly: interestOnly,
    })
    window.history.replaceState(null, "", `?${params}`)
  }, [price, deposit, rate, term, interestOnly])

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}?${encodeCalculatorParams({ propertyPrice: price, depositPercent: deposit, interestRate: rate, termYears: term, isInterestOnly: interestOnly })}`
      : ""
  const shareTitle = `Monthly mortgage: ${formatCurrencyPence(result.monthlyPayment)}/mo on a ${formatCurrency(price)} property at ${rate.toFixed(1)}%`

  const handleDownloadPdf = () =>
    generateCalculatorPdf({
      title: "Mortgage Calculation",
      subtitle: `${term}-year ${interestOnly ? "interest-only" : "repayment"} mortgage at ${rate.toFixed(1)}%`,
      sections: [
        {
          title: "Results",
          rows: [
            { label: "Monthly payment", value: formatCurrencyPence(result.monthlyPayment), sub: interestOnly ? "Interest only" : "Capital and interest", accent: true },
            { label: "Loan amount", value: formatCurrency(result.loanAmount), sub: `${result.ltv}% LTV` },
            { label: "Deposit", value: formatCurrency(result.depositAmount), sub: `${deposit}% of price` },
            { label: "Total interest", value: formatCurrency(result.totalInterest), sub: `Over ${term} years` },
            { label: "Total repaid", value: formatCurrency(result.totalRepaid), sub: "Loan + interest" },
          ],
        },
      ],
      url: shareUrl,
      generatedAt: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
    })

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Mortgage Repayment Calculator",
            description: meta.description,
            applicationCategory: "FinanceApplication",
            operatingSystem: "Web",
            offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
            provider: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://quiteright.co.uk",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Tools", path: "/tools" }, { name: "Mortgage Calculator" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Calculator
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Mortgage calculator
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Calculate your monthly mortgage repayments, total interest paid,
            and loan-to-value ratio for UK property purchases.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
            {/* Input panel */}
            <div className="flex-1 space-y-8">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Your mortgage<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Adjust the sliders to match your purchase.
                </p>
              </div>

              <CalculatorSlider
                label="Property price"
                value={price}
                min={50_000}
                max={2_000_000}
                step={5_000}
                formatValue={(v) => formatCurrency(v)}
                onChange={setPrice}
              />

              <CalculatorSlider
                label="Deposit"
                value={deposit}
                min={5}
                max={50}
                step={1}
                formatValue={(v) => `${v}% (${formatCurrency(price * v / 100)})`}
                onChange={setDeposit}
              />

              <CalculatorSlider
                label="Interest rate"
                value={rate}
                min={1}
                max={10}
                step={0.1}
                formatValue={(v) => `${v.toFixed(1)}%`}
                onChange={setRate}
              />

              <CalculatorSlider
                label="Mortgage term"
                value={term}
                min={5}
                max={40}
                step={1}
                formatValue={(v) => `${v} years`}
                onChange={setTerm}
              />

              {/* Repayment type toggle */}
              <div>
                <label className="text-sm font-medium text-charcoal">
                  Repayment type
                </label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {[
                    { value: false, label: "Repayment", desc: "Pay off the full loan" },
                    { value: true, label: "Interest only", desc: "Pay interest, repay loan later" },
                  ].map((option) => (
                    <button
                      key={option.label}
                      type="button"
                      onClick={() => setInterestOnly(option.value)}
                      className={`rounded-lg border px-3 py-3 text-left transition-all ${
                        interestOnly === option.value
                          ? "border-red bg-red/5 ring-1 ring-red/20"
                          : "border-stone bg-white hover:border-stone/80"
                      }`}
                    >
                      <p
                        className={`text-sm font-semibold ${
                          interestOnly === option.value ? "text-red" : "text-charcoal"
                        }`}
                      >
                        {option.label}
                      </p>
                      <p className="mt-0.5 text-xs text-warm-grey">{option.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Results panel */}
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Your repayments<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Based on a {result.ltv}% loan-to-value ratio.
                </p>
              </div>

              <CalculatorActionsBar shareUrl={shareUrl} shareTitle={shareTitle} onDownloadPdf={handleDownloadPdf} />

              <CalculatorResultCard
                label="Monthly payment"
                value={formatCurrencyPence(result.monthlyPayment)}
                sub={interestOnly ? "Interest only — loan balance unchanged" : "Capital and interest"}
                accent
                large
              />

              <div className="grid grid-cols-2 gap-4">
                <CalculatorResultCard
                  label="Loan amount"
                  value={formatCurrency(result.loanAmount)}
                  sub={`${result.ltv}% LTV`}
                />
                <CalculatorResultCard
                  label="Deposit"
                  value={formatCurrency(result.depositAmount)}
                  sub={`${deposit}% of price`}
                />
                <CalculatorResultCard
                  label="Total interest"
                  value={formatCurrency(result.totalInterest)}
                  sub={`Over ${term} years`}
                />
                <CalculatorResultCard
                  label="Total repaid"
                  value={formatCurrency(result.totalRepaid)}
                  sub="Loan + interest"
                />
              </div>

              {!interestOnly && (
                <PaymentSplitBar
                  loanAmount={result.loanAmount}
                  totalInterest={result.totalInterest}
                />
              )}

              {/* Interest-only comparison */}
              {!interestOnly && result.monthlyInterestOnly > 0 && (
                <div className="rounded-xl border border-stone bg-white p-5">
                  <p className="text-xs font-semibold uppercase tracking-[0.15em] text-charcoal">
                    Interest-only comparison
                  </p>
                  <p className="mt-2 text-sm text-slate">
                    On interest-only, you&rsquo;d pay{" "}
                    <span className="font-semibold text-charcoal">
                      {formatCurrencyPence(result.monthlyInterestOnly)}
                    </span>{" "}
                    per month — {formatCurrency(Math.round(result.monthlyPayment - result.monthlyInterestOnly))}{" "}
                    less, but the {formatCurrency(result.loanAmount)} loan remains at the end.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            How mortgage repayments work<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              With a repayment mortgage, each monthly payment is split between
              interest (charged on the remaining balance) and capital
              repayment. In the early years most of your payment goes towards
              interest, but as the balance falls the capital portion grows.
            </p>
            <p>
              Interest-only mortgages charge a lower monthly amount because
              you&rsquo;re not repaying any capital. The full loan must be
              settled at the end of the term — typically through the sale
              of the property or other investments.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "73%", label: "of buyers watch property video before viewing" },
            { stat: "2x", label: "more enquiries on listings with video" },
            { stat: "25 yrs", label: "is the most common UK mortgage term" },
            { stat: "<60s", label: "to create a listing video with QuiteRight" },
          ].map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related tools */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related tools<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              href="/tools/stamp-duty-calculator"
              className="group rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/20 hover:shadow-sm"
            >
              <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                Stamp duty calculator
              </p>
              <p className="mt-1 text-sm text-slate">
                Calculate SDLT on your UK property purchase.
              </p>
            </Link>
            <Link
              href="/tools/rental-yield-calculator"
              className="group rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/20 hover:shadow-sm"
            >
              <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                Rental yield calculator
              </p>
              <p className="mt-1 text-sm text-slate">
                Calculate gross and net yield for buy-to-let properties.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            See your next home come to life
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            The best estate agents bring properties to life with video.
            Browse video examples created by QuiteRight and see the
            difference.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/examples"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              See video examples
            </Link>
            <Link
              href="/directory"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-8 py-4 text-lg font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              Find an estate agent
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  )
}
